/**
 * News Preferences Store — Zustand store for news feed settings
 * Persists to localStorage
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface NewsState {
  // Preferences
  country: string;
  categories: string[];
  keywords: string[];

  // User interactions
  bookmarkedIds: string[];
  readIds: string[];

  // Actions — preferences
  setCountry: (country: string) => void;
  toggleCategory: (category: string) => void;
  setCategories: (categories: string[]) => void;
  addKeyword: (keyword: string) => void;
  removeKeyword: (keyword: string) => void;

  // Actions — interactions
  toggleBookmark: (articleId: string) => void;
  markAsRead: (articleId: string) => void;
  isBookmarked: (articleId: string) => boolean;
  isRead: (articleId: string) => boolean;

  // Reset
  resetPreferences: () => void;
}

const DEFAULT_STATE = {
  country: 'be',
  categories: ['finance'],
  keywords: [],
  bookmarkedIds: [],
  readIds: [],
};

export const useNewsStore = create<NewsState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      setCountry: (country) => set({ country }),

      toggleCategory: (category) => {
        const current = get().categories;
        if (current.includes(category)) {
          // Don't allow empty — keep at least one
          if (current.length > 1) {
            set({ categories: current.filter((c) => c !== category) });
          }
        } else {
          set({ categories: [...current, category] });
        }
      },

      setCategories: (categories) => {
        if (categories.length > 0) {
          set({ categories });
        }
      },

      addKeyword: (keyword) => {
        const trimmed = keyword.trim().toLowerCase();
        if (trimmed && !get().keywords.includes(trimmed)) {
          set({ keywords: [...get().keywords, trimmed] });
        }
      },

      removeKeyword: (keyword) => {
        set({ keywords: get().keywords.filter((k) => k !== keyword) });
      },

      toggleBookmark: (articleId) => {
        const current = get().bookmarkedIds;
        if (current.includes(articleId)) {
          set({ bookmarkedIds: current.filter((id) => id !== articleId) });
        } else {
          set({ bookmarkedIds: [...current, articleId] });
        }
      },

      markAsRead: (articleId) => {
        if (!get().readIds.includes(articleId)) {
          set({ readIds: [...get().readIds, articleId] });
        }
      },

      isBookmarked: (articleId) => get().bookmarkedIds.includes(articleId),
      isRead: (articleId) => get().readIds.includes(articleId),

      resetPreferences: () => set(DEFAULT_STATE),
    }),
    {
      name: 'credia-news-prefs',
    }
  )
);
